/**
 * Reading Mode Context
 *
 * Provides global reading mode state management.
 * When enabled, applies optimized typography and colors for reading.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

const STORAGE_KEY = 'medinterpret_reading_mode';

interface ReadingModeContextValue {
  isReadingMode: boolean;
  toggleReadingMode: () => void;
  setReadingMode: (enabled: boolean) => void;
}

const ReadingModeContext = createContext<ReadingModeContextValue | undefined>(undefined);

export const ReadingModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isReadingMode, setIsReadingMode] = useState<boolean>(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  });

  // Apply/remove class on body when state changes
  useEffect(() => {
    if (isReadingMode) {
      document.body.classList.add('reading-mode');
    } else {
      document.body.classList.remove('reading-mode');
    }
  }, [isReadingMode]);

  // Persist to localStorage when state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(isReadingMode));
  }, [isReadingMode]);

  const toggleReadingMode = useCallback(() => {
    setIsReadingMode((prev) => !prev);
  }, []);

  const setReadingMode = useCallback((enabled: boolean) => {
    setIsReadingMode(enabled);
  }, []);

  return (
    <ReadingModeContext.Provider value={{ isReadingMode, toggleReadingMode, setReadingMode }}>
      {children}
    </ReadingModeContext.Provider>
  );
};

export const useReadingMode = (): ReadingModeContextValue => {
  const context = useContext(ReadingModeContext);
  if (!context) {
    throw new Error('useReadingMode must be used within a ReadingModeProvider');
  }
  return context;
};
